import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import SortSquareIcon from '../../../static/img/sort-square.svg';
import RedCrossIcon from '../../../static/icons/red-cross.svg';

const SortByControl = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const hasSort = value === 'newestFirst' || value === 'oldestFirst';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Container ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
      <TriggerButton
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        isActive={hasSort}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={`Sort by${hasSort ? ', active' : ''}`}
      >
        <IconImg src={SortSquareIcon} alt="Sort" />
        <span data-dd-privacy="mask-user-input">Sort by</span>
        <Chevron isOpen={isOpen}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Chevron>
      </TriggerButton>
      {isOpen && (
        <Popup>
          <PopupHeader>
            <PopupTitle>
              <IconImg src={SortSquareIcon} alt="Sort by" />
              <span data-dd-privacy="mask-user-input">Sort by</span>
            </PopupTitle>
            {hasSort && (
              <ClearButton type="button" onClick={() => { onChange(null); setIsOpen(false); }}>
                <ClearIcon>
                  <img src={RedCrossIcon} alt="" />
                </ClearIcon>
                <ClearText>Clear all</ClearText>
              </ClearButton>
            )}
          </PopupHeader>
          <OptionsRow>
            <OptionPill active={value === 'newestFirst'} onClick={() => { onChange('newestFirst'); setIsOpen(false); }}>
              <span data-dd-privacy="mask-user-input">New to Old</span>
            </OptionPill>
            <OptionPill active={value === 'oldestFirst'} onClick={() => { onChange('oldestFirst'); setIsOpen(false); }}>
              <span data-dd-privacy="mask-user-input">Old to New</span>
            </OptionPill>
          </OptionsRow>
        </Popup>
      )}
    </Container>
  );
};

const Container = styled.div`position: relative;`;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(p) => (p.isActive ? '#F3E5F5' : '#ffffff')};
  border: 1px solid ${(p) => (p.isActive ? '#7B1FA2' : '#c9c6cc')};
  color: ${(p) => (p.isActive ? '#7B1FA2' : '#4b464f')};
  &:hover {
    border-color: #7B1FA2;
    background: #FCE4EC;
  }
`;

const IconImg = styled.img`width: 16px; height: 16px;`;

const Chevron = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: ${(p) => (p.isOpen ? 'rotate(180deg)' : 'rotate(0)')};
`;

const Popup = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 280px;
  width: max-content;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #E1BEE7;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 1000;
  padding: 12px 0;
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
`;

const PopupTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #7B1FA2;
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  &:hover { background: #FEE2E2; }
`;

const ClearIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid var(--color-danger-red-300, #E2483D);
  img { width: 12px; height: 12px; }
`;

const ClearText = styled.span`color: #E2483D;`;

const OptionsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 8px;
  padding: 0 16px 12px;
  align-items: center;
  min-width: min-content;
`;

const OptionPill = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid ${(p) => (p.active ? '#7200E0' : '#d1d5db')};
  background: ${(p) => (p.active ? '#7200E0' : '#fff')};
  color: ${(p) => (p.active ? '#fff' : '#374151')};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  width: max-content;
  min-width: max-content;
  flex-shrink: 0;
  &:hover {
    border-color: #7200E0;
    color: ${(p) => (p.active ? '#fff' : '#7200E0')};
    background: ${(p) => (p.active ? '#7200E0' : '#fff')};
  }
`;

export default SortByControl;
